import { Helmet } from 'react-helmet-async';
import { Navigate, Route, Routes, useParams } from 'react-router-dom';
import PageHeader from './components/PageHeader';
import React, { Suspense, useEffect, useRef } from 'react';
import { observer } from 'mobx-react';
import { Box, Grid, Typography, useMediaQuery, useTheme } from '@mui/material';
import { Assignment } from '@mui/icons-material';
import BusinessIcon from '@mui/icons-material/Business';
import ChatIcon from '@mui/icons-material/Chat';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import { useDependency } from '../../injections';
import { GeneralStore } from '../../mesh/stores/GeneralStore';

const MeshCompany = React.lazy(() =>
  import('./MeshCompany').then((module) => ({ default: module.MeshCompany }))
);
const MeshGPT = React.lazy(() =>
  import('./chat/MeshGPT').then((module) => ({ default: module.MeshGPT }))
);
const MeshBid = React.lazy(() =>
  import('./bid/MeshBid').then((module) => ({ default: module.MeshBid }))
);
const MeshSesam = React.lazy(() =>
  import('./MeshSesam').then((module) => ({ default: module.MeshSesam }))
);
const MeshRagAdmin = React.lazy(() =>
  import('./MeshRagAdmin').then((module) => ({
    default: module.MeshRagAdmin
  }))
);

const mainViews = [
  {
    id: 'chatbot',
    title: 'x1F GPT',
    description:
      '„Das datenschutzkonforme ChatGPT der x1F – maßgeschneiderte und spezialisierte Chatbots für alle unsere Bedürfnisse ',

    icon: <ChatIcon fontSize="large" />,
    component: <MeshGPT />
  }

  // {
  //   id: 'profil',
  //   title: 'Profil-Suche',
  //   description: 'Finde das ideale Unternehmen für ein bestimmtes Profil.',
  //   icon: <BusinessIcon fontSize="large" />,
  //   component: <MeshSales />
  // },
];

const sourceViews = [
  // { id: 'news', title: 'News', icon: <NewspaperIcon fontSize="medium" />, component: <MeshNews /> },
  // { id: 'jobs', title: 'Jobs', icon: <WorkIcon fontSize="medium" />, component: <MeshJobs /> }
];

const otherViews = [
  {
    id: 'company',
    title: 'Company Review',
    description: 'Erhalte detaillierte Insights zu unseren Kunden.',
    icon: <BusinessIcon fontSize="large" />,
    component: <MeshCompany />
  },
  {
    id: 'bid',
    title: 'SmartBid',
    description: 'Experten Tool für intelligente Analyse von Ausschreibungen.',
    icon: <Assignment fontSize="large" />,
    component: <MeshBid />
  },
  {
    id: 'sesam',
    title: 'Sesam',
    description: 'Suche SLG-Dokumente per Volltext, Hybrid Search und Agent.',
    permission: 'SESAM',
    icon: <ManageSearchIcon fontSize="large" />,
    component: <MeshSesam />
  },
  {
    id: 'admin',
    title: 'Admin',
    description: 'Wissensbasis und Nutzungsschätzungen verwalten.',
    permission: 'ADMIN',
    icon: <AdminPanelSettingsIcon fontSize="large" />,
    component: <MeshRagAdmin />
  }
];

const getViewPermission = (view: any): string | undefined => view.permission;
const VERSION_HEARTBEAT_INTERVAL_MS = 5 * 60 * 1000;

export function useHeartbeat() {
  const store = useDependency<GeneralStore>(GeneralStore);

  useEffect(() => {
    const refreshVersion = () => {
      void store.fetchVersion();
    };

    refreshVersion();
    const interval = window.setInterval(
      refreshVersion,
      VERSION_HEARTBEAT_INTERVAL_MS
    );

    return () => window.clearInterval(interval);
  }, [store]);
}

const DashboardHome = observer(function DashboardHome() {
  const store = useDependency<GeneralStore>(GeneralStore);

  useHeartbeat();

  useEffect(() => {
    if (!store.permissionsLoaded) {
      store.getPermissions();
    }
  }, [store, store.permissionsLoaded]);

  return (
    <Suspense fallback={<Typography>Lade Chat...</Typography>}>
      <MeshGPT />
    </Suspense>
  );
});

const DashboardDetail = observer(function DashboardDetail() {
  const { viewId } = useParams();
  const store = useDependency<GeneralStore>(GeneralStore);
  const trackedViewRef = useRef<string>();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const view = [...mainViews, ...sourceViews, ...otherViews].find(
    (v) => v.id === viewId
  );
  const permission = view ? getViewPermission(view) : undefined;

  useEffect(() => {
    if (!store.permissionsLoaded) {
      store.getPermissions();
    }
  }, [store, store.permissionsLoaded]);

  useEffect(() => {
    if (viewId && viewId !== 'chatbot' && store.messages.length > 0) {
      store.clearCurrentChat();
    }
  }, [store, viewId, store.messages.length]);

  useEffect(() => {
    if (!viewId || !['sesam', 'company', 'bid'].includes(viewId)) return;
    if (permission && (!store.permissionsLoaded || !store.hasPermission(permission))) {
      return;
    }
    if (trackedViewRef.current === viewId) return;

    trackedViewRef.current = viewId;
    void store.trackFeatureView(viewId as 'sesam' | 'company' | 'bid');
  }, [permission, store, store.permissionsLoaded, viewId]);

  if (!view) {
    return <Typography>Ansicht "{viewId}" nicht gefunden</Typography>;
  }

  if (permission && store.permissionsError) {
    return (
      <Typography>
        Berechtigungen konnten nicht geladen werden: {store.permissionsError}
      </Typography>
    );
  }

  if (permission && !store.permissionsLoaded) {
    return <Typography>Berechtigungen werden geladen...</Typography>;
  }

  if (permission && !store.hasPermission(permission)) {
    return <Typography>Keine Berechtigung für "{view.title}".</Typography>;
  }

  if (isMobile && view.id !== 'chatbot') {
    return (
      <Box
        sx={{
          minHeight: 'calc(100vh - var(--cockpit-header-height))',
          p: 3,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          color: 'var(--cockpit-text)',
          background: 'var(--cockpit-canvas)'
        }}
      >
        Diese Anwendung ist auf Smartphones derzeit nur für den Chat verfügbar.
      </Box>
    );
  }

  return (
    <Suspense fallback={<Typography>Lade Ansicht...</Typography>}>
      {view.component}
    </Suspense>
  );
});

function DashboardAC() {
  return (
    <>
      <Helmet>
        <title>AI-Cockpit</title>
      </Helmet>
      <PageHeader />

      <Grid sx={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <Grid maxWidth="100%" sx={{ flex: 1, minHeight: 0 }}>
          <Routes>
            <Route index element={<DashboardHome />} />
            <Route path="rag-admin" element={<Navigate to="/admin" replace />} />
            <Route path=":viewId" element={<DashboardDetail />} />
          </Routes>
        </Grid>
      </Grid>
    </>
  );
}

export const Dashboard = observer(DashboardAC);
